import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormLabel,
  Heading,
  InputGroup,
  InputRightElement,
  Switch,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import InputField from "../TextInputField";
import { useEffect, useState } from "react";
import BackButton from "../BackButton";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProductById,
  createProduct,
  updateProduct,
} from "@/services/product.service";
import { Product, ProductType } from "../../models/product.model";

import SelectComponent from "../SelectComponent";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";

export default function ProductRegisterForm() {
  const [actualProduct, setActualProduct] = useState<Product | undefined>(
    undefined
  );

  const toast = useToast();
  const navigate = useNavigate();

  const { productId } = useParams();
  const isNew = productId === "new";

  const productTypeOptions = Object.keys(ProductType).map((key) => ({
    value: ProductType[key as keyof typeof ProductType],
    label: ProductType[key as keyof typeof ProductType],
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!isNew) {
          const product = await getProductById(productId!);
          if (product) setActualProduct(product);
        }
      } catch (err: any) {
        toast({
          title: "Erro ao carregar dados",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchData();
  }, [productId]);

  return (
    <Flex direction="column" width="100%">
      <BackButton routeTo="/produtos" />

      <Card width="100%" display="flex" flexDir="column">
        <CardHeader width="100%">
          <Heading fontFamily="Lexend Deca" fontSize="3xl" as="b">
            {!isNew ? "Editar Produto" : "Adicionar Produto"}
          </Heading>
        </CardHeader>

        <CardBody height="100%">
          <Formik
            initialValues={{
              id: actualProduct?.id ?? undefined,
              name: actualProduct?.name ?? "",
              type: (actualProduct?.type as ProductType) ?? "",
              percentage: actualProduct?.percentage
                ? parseFloat((actualProduct.percentage * 100).toFixed(3))
                : 0,
              partnerPercentage: actualProduct?.partnerPercentage
                ? parseFloat((actualProduct.partnerPercentage * 100).toFixed(3))
                : 0,
              minLimit: actualProduct?.minLimit ? actualProduct.minLimit : 0,
              maxLimit: actualProduct?.maxLimit ? actualProduct.maxLimit : 0,
              enabled: actualProduct?.enabled ?? true,
            }}
            enableReinitialize
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);

              const productData: Product = {
                id: values.id,
                name: values.name.toUpperCase(),
                type: values.type,
                percentage: parseFloat((values.percentage / 100).toFixed(2)),
                partnerPercentage: parseFloat(
                  (values.partnerPercentage / 100).toFixed(2)
                ),
                minLimit: values.minLimit,
                maxLimit: values.maxLimit,
                enabled: values.enabled,
              };

              try {
                !isNew
                  ? await updateProduct(values.id!, productData)
                  : await createProduct(productData);
                toast({
                  title: `Produto ${!isNew ? "editado" : "cadastrado"} com sucesso`,
                  description: `Produto ${productData.name} foi ${!isNew ? "editado" : "cadastrado"}!`,
                  status: "success",
                  duration: 3000,
                  isClosable: true,
                });

                navigate("/produtos");
              } catch (error: any) {
                toast({
                  title: "Erro ao salvar produto",
                  description: error.message,
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                });
              }
            }}
          >
            {({ errors, touched, setFieldValue, values, isSubmitting }) => (
              <Form>
                <VStack spacing={8} minW="100%">
                  <Flex gap="2rem" width="100%">
                    <InputField
                      name="name"
                      label="Nome do Produto"
                      placeholder="Nome do Produto"
                      value={values.name}
                      error={errors.name}
                      touched={touched.name}
                      setFieldValue={setFieldValue}
                      isUpperCase
                    />

                    <Field
                      name="type"
                      label="Tipo"
                      component={SelectComponent}
                      options={productTypeOptions}
                      valueKey="value"
                      labelKey="label"
                      placeholder="Selecione o Tipo"
                      isLoading={isSubmitting}
                    />
                  </Flex>

                  <Flex gap="2rem" width="100%">
                    <InputGroup>
                      <InputField
                        name="percentage"
                        label="Percentual"
                        type="number"
                        placeholder="Percentual"
                        value={values.percentage}
                        error={errors.percentage}
                        touched={touched.percentage}
                        setFieldValue={setFieldValue}
                      />
                      <InputRightElement
                        pointerEvents="none"
                        color="black"
                        fontSize="sm"
                        right="0.5rem"
                        top="45%"
                      >
                        %
                      </InputRightElement>
                    </InputGroup>

                    <InputGroup>
                      <InputField
                        name="partnerPercentage"
                        label="Percentual de Comissão"
                        type="number"
                        placeholder="Percentual de Comissão"
                        value={values.partnerPercentage}
                        error={errors.partnerPercentage}
                        touched={touched.partnerPercentage}
                        setFieldValue={setFieldValue}
                      />
                      <InputRightElement
                        pointerEvents="none"
                        color="black"
                        fontSize="sm"
                        right="0.5rem"
                        top="45%"
                      >
                        %
                      </InputRightElement>
                    </InputGroup>

                    <InputField
                      name="minLimit"
                      label="Limite Mínimo"
                      type="text"
                      placeholder="Limite Mínimo"
                      value={BrlCurrencyFormatter(values.minLimit)}
                      error={errors.minLimit}
                      touched={touched.minLimit}
                      setFieldValue={setFieldValue}
                    />

                    <InputField
                      name="maxLimit"
                      label="Limite Máximo"
                      type="text"
                      placeholder="Limite Máximo"
                      value={BrlCurrencyFormatter(values.maxLimit)}
                      error={errors.maxLimit}
                      touched={touched.maxLimit}
                      setFieldValue={setFieldValue}
                    />
                  </Flex>

                  <Flex gap="1rem" width="100%" alignItems="center">
                    <FormLabel htmlFor="enabled">Ativo</FormLabel>
                    <Switch
                      id="enabled"
                      name="enabled"
                      isChecked={values.enabled}
                      onChange={(e) =>
                        setFieldValue("enabled", e.target.checked)
                      }
                      colorScheme="green"
                    />
                  </Flex>

                  <Flex justify="center" gap="2rem">
                    {/* {!isNew && (
                      <DeleteButton
                        deleteService={deleteProduct}
                        id={values.id!}
                        navigateRoute="/produtos"
                        entityName="Produto"
                        entityIdentifier={values.name}
                        buttonProps={{
                          isLoading: isSubmitting,
                          loadingText: "Desativando",
                          style: {
                            backgroundColor: "red",
                            color: "white",
                            width: "16rem",
                          },
                        }}
                      />
                    )} */}

                    <Button
                      color={"white"}
                      type="submit"
                      bg={"secondary"}
                      _hover={{ bg: "#1e3f5b" }}
                      width="16rem"
                      isLoading={isSubmitting}
                      loadingText="Salvando"
                    >
                      {!isNew ? "Salvar Alterações" : "Cadastrar Produto"}
                    </Button>
                  </Flex>
                </VStack>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </Flex>
  );
}
